// Location-timezone helpers (IANA, e.g. "America/Los_Angeles").
//
// Philosophy (product decision): a sale belongs to the LOCATION'S local day
// and hour, NOT to the timezone of whoever is consulting the report. A Vegas
// restaurant's numbers look the same whether the owner checks from Vegas or
// from Paris. All sales reporting buckets/ranges must go through these.
//
// No external deps — everything derives from Intl.

const FALLBACK_TZ = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** The report timezone for a location doc (falls back to the viewer's). */
export const reportTz = (location) => location?.timezone || FALLBACK_TZ();

/** Location-local calendar date of an instant: 'YYYY-MM-DD'. */
export function tzDateKey(date, tz) {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return dtf.format(date); // en-CA gives YYYY-MM-DD directly
}

/** Location-local hour (0-23) of an instant. */
export function tzHour(date, tz) {
  const h = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', hour12: false,
  }).format(date);
  const n = parseInt(h, 10);
  return n === 24 ? 0 : n; // midnight shows as "24" in some engines
}

/** Minutes the location is AHEAD of UTC at a given instant (handles DST). */
export function tzOffsetMinutesAt(utcInstant, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(utcInstant).map((p) => [p.type, p.value]));
  const asIfUtc = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    +parts.hour === 24 ? 0 : +parts.hour, +parts.minute, +parts.second
  );
  return Math.round((asIfUtc - utcInstant.getTime()) / 60000);
}

/** [startUtc, endUtc] Date pair covering the location-local day 'YYYY-MM-DD'. */
export function tzDayRangeUtc(dateStr, tz) {
  // Evaluate the offset at local noon so DST edges never bite at midnight.
  const noonUtc = new Date(`${dateStr}T12:00:00Z`);
  const off = tzOffsetMinutesAt(noonUtc, tz);
  const start = new Date(new Date(`${dateStr}T00:00:00Z`).getTime() - off * 60000);
  const end = new Date(new Date(`${dateStr}T23:59:59.999Z`).getTime() - off * 60000);
  return [start, end];
}

/** True when instant falls inside the location-local day 'YYYY-MM-DD'. */
export function isOnTzDay(instant, dateStr, tz) {
  return tzDateKey(instant, tz) === dateStr;
}

/** The location-local 'YYYY-MM-DD' for "today" in that timezone. */
export function tzToday(tz) {
  return tzDateKey(new Date(), tz);
}

/** Shift a 'YYYY-MM-DD' by N days (string math, DST-safe via UTC noon). */
export function tzShiftDays(dateStr, days) {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

/** Common US timezones for the location picker (+ detect button uses Intl). */
export const COMMON_TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Phoenix', 'America/Chicago',
  'America/New_York', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Tijuana', 'America/Mexico_City', 'America/Bogota', 'America/Lima',
  'America/Santiago', 'America/Argentina/Buenos_Aires', 'America/Sao_Paulo',
  'Europe/Madrid', 'Europe/Paris', 'UTC',
];
